import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Chip,
  FormHelperText,
  IconButton,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { Email, Flag, Person, Phone, Title, Description, Lock, Close as CloseIcon } from '@mui/icons-material';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { REPORT_REASONS } from '../../constants/suspiciousActivityConstants'


const SuspiciousReportDialog = ({
  open,
  onClose,
  onReportSuccess,
  entityType = 'campaign', // 'campaign', 'event', or 'group'
  entityId,
  groupType
}) => {
  const { user } = useAuth();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [reportReason, setReportReason] = useState('');
  const [customReason, setCustomReason] = useState('');
  const [reportDescription, setReportDescription] = useState('');
  const [reportEmail, setReportEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [touchedFields, setTouchedFields] = useState({});
  const [showAllErrors, setShowAllErrors] = useState(false);
  const [shouldValidate, setShouldValidate] = useState(false);

  // Pre-fill user data when dialog opens and user is logged in
  useEffect(() => {
    if (open && user) {
      setFirstName(user.firstName || '');
      setLastName(user.lastName || '');
      setReportEmail(user.email || '');
      setPhoneNumber(user.phoneNumber || '');
      // Enable validation after user data is loaded
      setTimeout(() => setShouldValidate(true), 100);
    } else if (open && !user) {
      // Enable validation for non-logged-in users after dialog opens
      setTimeout(() => setShouldValidate(true), 100);
    }
  }, [open, user]);

  // Reset form when dialog closes
  useEffect(() => {
    if (!open) {
      setReportReason('');
      setCustomReason('');
      setReportDescription('');
      setReportEmail('');
      setFirstName('');
      setLastName('');
      setPhoneNumber('');
      setError('');
      setFieldErrors({});
      setTouchedFields({});
      setShowAllErrors(false);
      setShouldValidate(false);
    }
  }, [open]);

  // Validation functions
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email) return 'Email is required';
    if (!emailRegex.test(email)) return 'Please enter a valid email address';
    return '';
  };

  const validateFirstName = (name) => {
    if (!name.trim()) return 'First name is required';
    if (name.trim().length < 2) return 'First name must be at least 2 characters';
    return '';
  };

  const validateLastName = (name) => {
    if (!name.trim()) return 'Last name is required';
    if (name.trim().length < 2) return 'Last name must be at least 2 characters';
    return '';
  };

  const validatePhone = (phone) => {
    if (!phone || phone.trim() === '') return ''; // Phone is optional
    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.length === 0) return ''; // Empty after cleaning is valid (optional field)
    if (cleaned.length < 10) return 'Phone number must be at least 10 digits';
    if (cleaned.length > 15) return 'Phone number must be less than 16 digits';
    return '';
  };

  const validateReason = (reason) => {
    if (!reason) return 'Please select a reason for reporting';
    return '';
  };

  const validateCustomReason = (reason, mainReason) => {
    if (mainReason === 'Other' && !reason.trim()) {
      return 'Please provide a title for your report';
    }
    return '';
  };

  const validateDescription = (description) => {
    if (!description.trim()) return 'Please provide a detailed description of the issue';
    if (description.trim().length < 10) return 'Description must be at least 10 characters';
    if (description.trim().length > 1000) return 'Description must be less than 1000 characters';
    return '';
  };

  // Update field errors when values change
  useEffect(() => {
    // Only validate after shouldValidate is true
    if (!shouldValidate) return;
    
    const errors = {
      email: validateEmail(reportEmail),
      firstName: validateFirstName(firstName),
      lastName: validateLastName(lastName),
      phone: validatePhone(phoneNumber),
      reason: validateReason(reportReason),
      customReason: validateCustomReason(customReason, reportReason),
      description: validateDescription(reportDescription)
    };
    setFieldErrors(errors);
  }, [shouldValidate, reportEmail, firstName, lastName, phoneNumber, reportReason, customReason, reportDescription]);

  const handleFieldBlur = (fieldName) => {
    setTouchedFields(prev => ({ ...prev, [fieldName]: true }));
  };

  const hasErrors = () => {
    return Object.values(fieldErrors).some(error => error !== '');
  };

  const getFieldError = (fieldName) => {
    // Only show error if field was touched OR we're showing all errors (after submit attempt)
    return (touchedFields[fieldName] || showAllErrors) ? fieldErrors[fieldName] : '';
  };

  const shouldShowError = (fieldName) => {
    return (touchedFields[fieldName] || showAllErrors) && fieldErrors[fieldName];
  };

  const handleSubmit = async () => {
    // Check if there are any validation errors
    if (hasErrors()) {
      // Show all errors without marking fields as touched
      setShowAllErrors(true);
      setError('Please complete all required fields before submitting.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const reportTitle = reportReason === 'Other' ? customReason : reportReason;
      let payload = {
        title: reportTitle,
        description: reportDescription,
        email: reportEmail,
        firstName: firstName,
        lastName: lastName,
        phoneNumber: phoneNumber || null
      }
      switch (entityType) {
        case 'campaign':
          payload.campaignId = entityId;
          payload.reportType = entityType.toUpperCase();
          break;
        case 'event':
          payload.eventId = entityId;
          payload.reportType = entityType.toUpperCase();
          break;
        case 'group':
          payload.groupId = entityId;
          payload.reportType = entityType.toUpperCase();
          break;
        case 'user_ROSCA':
        case 'user_EDUCATIVE':
        case 'user_COMMUNITY_UPLIFT':
          payload.userId = entityId;
          payload.reportType = 'USER';
          break;
        default:
          console.error(`Unexpected entityType: ${entityType}`);
      }


      await api.post('/v1/api/suspicious-activity-reports', payload);

      // Reset form
      setReportReason('');
      setCustomReason('');
      setReportDescription('');
      setReportEmail('');
      setFirstName('');
      setLastName('');
      setPhoneNumber('');
      setTouchedFields({});
      setShowAllErrors(false);

      // Notify parent of success
      if (onReportSuccess) {
        onReportSuccess('Thank you for your report. We will review it shortly.');
      }

      // Close dialog
      if (onClose) {
        onClose();
      }
    } catch (err) {
      console.error('Error submitting report:', err);
      setError(
        err.response?.data?.message ||
        'Failed to submit report. Please try again later.'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    // Only allow closing via the Cancel button or successful submission
    if (!loading) {
      if (onClose) {
        onClose();
      }
    }
  };

  const isFormValid = () => {
    return !hasErrors() &&
      reportReason &&
      (reportReason !== 'Other' || customReason.trim()) && // If "Other" is selected, customReason must be filled
      reportDescription.trim() &&
      reportEmail.trim() &&
      firstName.trim() &&
      lastName.trim() &&
      !loading;
  };

  const getEntityLabel = () => {
    const labels = {
      campaign: 'campaign',
      event: 'event',
      group: 'group'
    };
    return labels[entityType] || 'item';
  };

  const handlePhoneChange = (e) => {
    const value = e.target.value.replace(/\D/g, '');
    if (value.length <= 15) {
      setPhoneNumber(value);
    }
  };

  const formatPhoneNumber = (phone) => {
    if (!phone) return '';
    const cleaned = phone.replace(/\D/g, '');
    const match = cleaned.match(/^(\d{1,3})?(\d{0,3})?(\d{0,4})?$/);
    if (match) {
      return [match[1], match[2], match[3]].filter(Boolean).join('-');
    }
    return phone;
  };

  // Get only the actual errors to display in summary
  const getActualErrors = () => {
    const errors = [];
    if (showAllErrors) {
      Object.entries(fieldErrors).forEach(([field, error]) => {
        if (error) {
          errors.push(error);
        }
      });
    }
    return errors;
  };

  // Get character count for description
  const getDescriptionCharacterCount = () => {
    return reportDescription.length;
  };

  // Helper to determine if user fields should be disabled
  const isUserFieldDisabled = () => {
    return !!user || loading;
  };

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        // Prevent closing on backdrop click or escape key
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
          return;
        }
        handleClose();
      }}
      maxWidth="md"
      fullWidth
      fullScreen={isSmallScreen}
      disableEscapeKeyDown // Prevent closing with ESC key
      PaperProps={{ 
        sx: { 
          borderRadius: { xs: 0, sm: 2 },
          maxHeight: { xs: '100vh', sm: '90vh' },
          m: { xs: 0, sm: 2 }
        } 
      }}
    >
      <DialogTitle
        sx={{
          bgcolor: 'primary.main',
          color: 'primary.contrastText',
          py: { xs: 1.5, sm: 2 },
          px: { xs: 2, sm: 3 },
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, sm: 1 } }}>
          <Flag sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }} />
          <Typography 
            variant="h6" 
            component="span" 
            sx={{ 
              fontWeight: 'bold',
              fontSize: { xs: '1rem', sm: '1.25rem' }
            }}
          >
            {isSmallScreen ? 'Report' : `Report ${getEntityLabel().charAt(0).toUpperCase() + getEntityLabel().slice(1)}`}
          </Typography>
        </Box>
        <IconButton
          onClick={handleClose}
          disabled={loading}
          sx={{
            color: 'primary.contrastText',
            padding: { xs: 0.5, sm: 1 },
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.1)'
            }
          }}
        >
          <CloseIcon sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }} />
        </IconButton>
      </DialogTitle>

      <DialogContent 
        sx={{ 
          p: { xs: 2, sm: 3 },
          overflowY: 'auto',
          '&::-webkit-scrollbar': {
            width: { xs: '4px', sm: '8px' }
          },
          '&::-webkit-scrollbar-track': {
            background: 'transparent'
          },
          '&::-webkit-scrollbar-thumb': {
            background: 'rgba(0,0,0,0.2)',
            borderRadius: '4px',
            '&:hover': {
              background: 'rgba(0,0,0,0.3)'
            }
          }
        }}
      >
        <Typography 
          variant="body2" 
          color="text.secondary" 
          sx={{ 
            mb: 3,
            fontSize: { xs: '0.875rem', sm: '0.875rem' }
          }}
        >
          {isSmallScreen 
            ? `Help us keep our community safe by reporting this ${getEntityLabel()}.`
            : `Please let us know why you're reporting this ${getEntityLabel()}. Your feedback helps us maintain a safe and trustworthy community.`
          }
        </Typography>

        {/* User Info Notice */}
        {user && (
          <Alert 
            severity="info" 
            sx={{ 
              mb: 2,
              '& .MuiAlert-message': {
                fontSize: { xs: '0.8rem', sm: '0.875rem' },
                padding: { xs: '4px 0', sm: '8px 0' }
              }
            }}
          >
            <Typography variant="body2" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
              {isSmallScreen 
                ? <><strong>Account info secured.</strong> Pre-filled for verification.</>
                : <><strong>Your account information is pre-filled and secured.</strong> This ensures we can properly identify and follow up on your report.</>
              }
            </Typography>
          </Alert>
        )}

        {/* Global Error Alert */}
        {error && (
          <Alert 
            severity="error" 
            sx={{ 
              mb: 2,
              '& .MuiAlert-message': {
                fontSize: { xs: '0.8rem', sm: '0.875rem' },
                padding: { xs: '4px 0', sm: '8px 0' }
              }
            }}
          >
            {error}
          </Alert>
        )}

        {/* Contact Information Section */}
        <Typography 
          variant="subtitle1" 
          sx={{ 
            mb: { xs: 1.5, sm: 2 }, 
            fontWeight: 600,
            fontSize: { xs: '1rem', sm: '1.125rem' }
          }}
        >
          Your Information
        </Typography>

        <Grid container spacing={{ xs: 1.5, sm: 2 }} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              variant="outlined"
              label="First Name"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              onBlur={() => handleFieldBlur('firstName')}
              placeholder="Enter your first name"
              error={shouldShowError('firstName')}
              helperText={getFieldError('firstName')}
              InputProps={{
                startAdornment: user ? <Lock sx={{ color: 'action.active', mr: { xs: 0.5, sm: 1 }, fontSize: { xs: '1rem', sm: '1.25rem' } }} /> : <Person sx={{ color: 'action.active', mr: { xs: 0.5, sm: 1 }, fontSize: { xs: '1rem', sm: '1.25rem' } }} />,
                sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
              }}
              InputLabelProps={{
                sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
              }}
              FormHelperTextProps={{
                sx: { fontSize: { xs: '0.75rem', sm: '0.75rem' } }
              }}
              disabled={isUserFieldDisabled()}
              required
              size={isSmallScreen ? 'small' : 'medium'}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              variant="outlined"
              label="Last Name"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              onBlur={() => handleFieldBlur('lastName')}
              placeholder="Enter your last name"
              error={shouldShowError('lastName')}
              helperText={getFieldError('lastName')}
              InputProps={{
                startAdornment: user ? <Lock sx={{ color: 'action.active', mr: { xs: 0.5, sm: 1 }, fontSize: { xs: '1rem', sm: '1.25rem' } }} /> : <Person sx={{ color: 'action.active', mr: { xs: 0.5, sm: 1 }, fontSize: { xs: '1rem', sm: '1.25rem' } }} />,
                sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
              }}
              InputLabelProps={{
                sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
              }}
              FormHelperTextProps={{
                sx: { fontSize: { xs: '0.75rem', sm: '0.75rem' } }
              }}
              disabled={isUserFieldDisabled()}
              required
              size={isSmallScreen ? 'small' : 'medium'}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              type="email"
              variant="outlined"
              label="Email Address"
              value={reportEmail}
              onChange={(e) => setReportEmail(e.target.value)}
              onBlur={() => handleFieldBlur('email')}
              placeholder="Enter your email address"
              error={shouldShowError('email')}
              helperText={getFieldError('email') || (isSmallScreen ? "Required for follow-up" : "We need your email to follow up on your report if necessary")}
              InputProps={{
                startAdornment: user ? <Lock sx={{ color: 'action.active', mr: { xs: 0.5, sm: 1 }, fontSize: { xs: '1rem', sm: '1.25rem' } }} /> : <Email sx={{ color: 'action.active', mr: { xs: 0.5, sm: 1 }, fontSize: { xs: '1rem', sm: '1.25rem' } }} />,
                sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
              }}
              InputLabelProps={{
                sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
              }}
              FormHelperTextProps={{
                sx: { fontSize: { xs: '0.75rem', sm: '0.75rem' } }
              }}
              disabled={isUserFieldDisabled()}
              required
              size={isSmallScreen ? 'small' : 'medium'}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              variant="outlined"
              label="Phone Number (Optional)"
              value={formatPhoneNumber(phoneNumber)}
              onChange={handlePhoneChange}
              onBlur={() => handleFieldBlur('phone')}
              placeholder="123-456-7890"
              error={shouldShowError('phone')}
              helperText={getFieldError('phone')}
              InputProps={{
                startAdornment: user ? <Lock sx={{ color: 'action.active', mr: { xs: 0.5, sm: 1 }, fontSize: { xs: '1rem', sm: '1.25rem' } }} /> : <Phone sx={{ color: 'action.active', mr: { xs: 0.5, sm: 1 }, fontSize: { xs: '1rem', sm: '1.25rem' } }} />,
                sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
              }}
              InputLabelProps={{
                sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
              }}
              FormHelperTextProps={{
                sx: { fontSize: { xs: '0.75rem', sm: '0.75rem' } }
              }}
              disabled={isUserFieldDisabled()}
              size={isSmallScreen ? 'small' : 'medium'}
            />
          </Grid>
        </Grid>

        {/* Report Details Section */}
        <Typography 
          variant="subtitle1" 
          sx={{ 
            mb: 2, 
            fontWeight: 600,
            fontSize: { xs: '1rem', sm: '1.125rem' }
          }}
        >
          Report Details
        </Typography>

        {/* Report Title/Reason Section */}
        <FormControl
          fullWidth
          sx={{ mb: 3 }}
          error={shouldShowError('reason')}
          size={isSmallScreen ? 'small' : 'medium'}
        >
          <InputLabel 
            id="report-reason-label"
            sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
          >
            Report Title *
          </InputLabel>
          <Select
            labelId="report-reason-label"
            value={reportReason}
            label="Report Title *"
            onChange={(e) => {
              setReportReason(e.target.value);
              handleFieldBlur('reason');
            }}
            onBlur={() => handleFieldBlur('reason')}
            disabled={loading}
            sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
          >
            {REPORT_REASONS[entityType]?.map((reason) => (
              <MenuItem 
                key={reason} 
                value={reason}
                sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
              >
                {reason}
              </MenuItem>
            ))}
          </Select>
          {shouldShowError('reason') && (
            <FormHelperText sx={{ fontSize: { xs: '0.75rem', sm: '0.75rem' } }}>
              {fieldErrors.reason}
            </FormHelperText>
          )}
          <FormHelperText sx={{ fontSize: { xs: '0.75rem', sm: '0.75rem' } }}>
            Select the main reason for your report
          </FormHelperText>
        </FormControl>

        {/* Custom Title Field */}
        {reportReason === 'Other' && (
          <TextField
            fullWidth
            variant="outlined"
            label="Custom Report Title *"
            value={customReason}
            onChange={(e) => setCustomReason(e.target.value)}
            onBlur={() => handleFieldBlur('customReason')}
            placeholder="Enter a title for your report..."
            error={shouldShowError('customReason')}
            helperText={getFieldError('customReason') || "Provide a clear title for your report"}
            InputProps={{
              startAdornment: <Title sx={{ color: 'action.active', mr: { xs: 0.5, sm: 1 }, fontSize: { xs: '1rem', sm: '1.25rem' } }} />,
              sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
            }}
            InputLabelProps={{
              sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
            }}
            FormHelperTextProps={{
              sx: { fontSize: { xs: '0.75rem', sm: '0.75rem' } }
            }}
            disabled={loading}
            sx={{ mb: 3 }}
            size={isSmallScreen ? 'small' : 'medium'}
          />
        )}

        {/* Report Description Field */}
        <TextField
          fullWidth
          multiline
          rows={isSmallScreen ? 3 : 4}
          variant="outlined"
          label="Report Description *"
          value={reportDescription}
          onChange={(e) => setReportDescription(e.target.value)}
          onBlur={() => handleFieldBlur('description')}
          placeholder={isSmallScreen ? "Describe the issue in detail..." : "Please provide a detailed description of the issue. Include specific examples, dates, and any relevant information that will help us understand the situation..."}
          error={shouldShowError('description')}
          helperText={
            getFieldError('description') ||
            `${isSmallScreen ? 'Details' : 'Provide detailed information about your report'} (${getDescriptionCharacterCount()}/1000)`
          }
          InputProps={{
            startAdornment: <Description sx={{ color: 'action.active', mr: { xs: 0.5, sm: 1 }, fontSize: { xs: '1rem', sm: '1.25rem' }, alignSelf: 'flex-start', mt: 1 }} />,
            sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
          }}
          InputLabelProps={{
            sx: { fontSize: { xs: '0.875rem', sm: '1rem' } }
          }}
          FormHelperTextProps={{
            sx: { fontSize: { xs: '0.75rem', sm: '0.75rem' } }
          }}
          disabled={loading}
          sx={{ mb: 2 }}
          size={isSmallScreen ? 'small' : 'medium'}
        />

        {/* Quick Select Chips for common reasons */}
        {!reportReason && (
          <Box sx={{ mb: 2 }}>
            <Typography 
              variant="body2" 
              color="text.secondary" 
              sx={{ 
                mb: 1,
                fontSize: { xs: '0.75rem', sm: '0.875rem' }
              }}
            >
              {isSmallScreen ? 'Quick select:' : 'Common report titles:'}
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: { xs: 0.5, sm: 1 } }}>
              {REPORT_REASONS[entityType]?.slice(0, 3).map((reason) => (
                <Chip
                  key={reason}
                  label={reason}
                  onClick={() => {
                    setReportReason(reason);
                    handleFieldBlur('reason');
                  }}
                  variant="outlined"
                  size="small"
                  sx={{ 
                    cursor: 'pointer',
                    fontSize: { xs: '0.7rem', sm: '0.8125rem' },
                    height: { xs: 24, sm: 28 },
                    '& .MuiChip-label': {
                      px: { xs: 1, sm: 1.5 },
                      fontSize: { xs: '0.7rem', sm: '0.8125rem' }
                    }
                  }}
                />
              ))}
            </Box>
          </Box>
        )}

        {/* Form Status Summary - Only show when there are actual errors and we're showing all */}
        {showAllErrors && getActualErrors().length > 0 && (
          <Alert 
            severity="warning" 
            sx={{ 
              mt: 2,
              '& .MuiAlert-message': {
                fontSize: { xs: '0.8rem', sm: '0.875rem' },
                padding: { xs: '4px 0', sm: '8px 0' }
              }
            }}
          >
            <Typography variant="body2" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
              {isSmallScreen ? 'Please fix these issues:' : 'Please fix the following issues before submitting:'}
            </Typography>
            <Box 
              component="ul" 
              sx={{ 
                mt: { xs: 0.5, sm: 1 }, 
                mb: 0, 
                pl: { xs: 1.5, sm: 2 }
              }}
            >
              {getActualErrors().map((error, index) => (
                <li key={index}>
                  <Typography variant="body2" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                    {error}
                  </Typography>
                </li>
              ))}
            </Box>
          </Alert>
        )}

        {/* Additional Context */}
        <Alert 
          severity="info" 
          sx={{ 
            mt: 2,
            '& .MuiAlert-message': {
              fontSize: { xs: '0.8rem', sm: '0.875rem' },
              padding: { xs: '4px 0', sm: '8px 0' }
            }
          }}
        >
          <Typography variant="body2" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
            {isSmallScreen ? (
              <>
                <strong>Note:</strong> We'll review your report within 24-48 hours and take appropriate action.
              </>
            ) : (
              <>
                <strong>Note:</strong> Your report will be reviewed by our team within 24-48 hours.
                We take all reports seriously and will take appropriate action based on our community guidelines.
                The more detailed your description, the better we can investigate.
              </>
            )}
          </Typography>
        </Alert>
      </DialogContent>

      <DialogActions sx={{ 
        p: { xs: 2, sm: 3 }, 
        pt: 0,
        flexDirection: { xs: 'column-reverse', sm: 'row' },
        gap: { xs: 1, sm: 0 },
        '& .MuiButton-root': {
          minHeight: { xs: 42, sm: 36 }
        }
      }}>
        <Button
          onClick={handleClose}
          sx={{ 
            borderRadius: 2,
            width: { xs: '100%', sm: 'auto' },
            fontSize: { xs: '0.875rem', sm: '0.875rem' },
            py: { xs: 1.2, sm: 0.8 }
          }}
          disabled={loading}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!isFormValid()}
          sx={{ 
            borderRadius: 2,
            width: { xs: '100%', sm: 'auto' },
            fontSize: { xs: '0.875rem', sm: '0.875rem' },
            py: { xs: 1.2, sm: 0.8 },
            fontWeight: { xs: 600, sm: 500 }
          }}
        >
          {loading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={16} sx={{ color: 'inherit' }} />
              Submitting...
            </Box>
          ) : (
            isSmallScreen ? 'Submit Report' : 'Submit Report'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SuspiciousReportDialog;